import { AccordionHeader, AccordionItem, AccordionPanel, Button, InfoLabel, Option, Text } from '@fluentui/react-components';
import { DeleteRegular } from '@fluentui/react-icons';
import { observer } from 'mobx-react-lite';
import React from 'react';
import { useCommonStyles, useStores } from '../../../common/hooks';
import { getTooltipContentByFieldName } from '../../../common/tooltip/tooltip-get-content';
import { CustomerReference } from '../../../models';
import { FlexBox } from '../../shared-components/flex-box';
import { FormInput } from '../../shared-components/form/FormInput';
import { SelectOptions } from '../../shared-components/form/SelectOptions';
import { PhoneNumberField, PhoneNumberInput } from '../../shared-components/phone-number-input';
import { ValidationMessageList } from '../../shared-components/ValidationMessageList';
import './CustomerReferenceFormInputs.scss';

interface CustomerReferenceFormInputsProps {
  rowKey: string;
  customerReference: CustomerReference;
  customerReferenceIndex: number;
  onRemoveCustomerReference(): void;
}

const CustomerReferenceFormInputsImpl: React.FC<CustomerReferenceFormInputsProps> = (props) => {
  const {
    rowKey,
    customerReference,
    customerReferenceIndex,
    onRemoveCustomerReference,
  } = props;

  const {
    nominationWizardPageUiStore,
  } = useStores();

  const isBeingValidated = nominationWizardPageUiStore.isBeingValidated('nomination-submission');

  const validation = nominationWizardPageUiStore.customerReferenceValidations[customerReferenceIndex];

  const commonStyles = useCommonStyles();

  const renderCustomerReferenceFormInputs = (): React.JSX.Element => {
    return (
      <>
        <FlexBox
          wrap
          isFullWidth
          aligns='start'
          className='panel__row'
        >
          <FlexBox
            isFullWidth
            aligns='start'
            className='row__sub-row'
          >
            <SelectOptions
              className='field--prefix'
              validationMessage={
                isBeingValidated
                && validation.fieldErrors.prefix?.length
                  ? (
                    <ValidationMessageList
                      messages={validation.fieldErrors.prefix}
                    />
                  )
                  : undefined
              }
              label='Prefix'
              onOptionSelect={(optionData) => customerReference.prefix = optionData.optionValue ? optionData.optionValue : ''}
              dropdownProps={{
                inlinePopup: true,
                value: customerReference.prefix,
                selectedOptions: [customerReference.prefix],
              }}
            >
              <Option value='' text='' />
              <Option value='Mr.'>Mr.</Option>
              <Option value='Mrs.'>Mrs.</Option>
              <Option value='Ms.'>Ms.</Option>
            </SelectOptions>

            <FormInput
              required
              label='First name'
              validationMessage={
                isBeingValidated
                && validation.fieldErrors.firstName?.length
                  ? (
                    <ValidationMessageList
                      messages={validation.fieldErrors.firstName}
                    />
                  )
                  : undefined
              }
              onChange={(firstName) => customerReference.firstName = firstName}
              inputProps={{
                type: 'text',
                maxLength: 100,
                value: customerReference.firstName,
              }}
            />
          </FlexBox>
          <FormInput
            required
            label='Last name'
            validationMessage={
              isBeingValidated
              && validation.fieldErrors.lastName?.length
                ? (
                  <ValidationMessageList
                    messages={validation.fieldErrors.lastName}
                  />
                )
                : undefined
            }
            onChange={(lastName) => customerReference.lastName = lastName}
            inputProps={{
              type: 'text',
              maxLength: 100,
              value: customerReference.lastName,
            }}
          />
        </FlexBox>

        <FlexBox
          isFullWidth
          aligns='start'
          className='panel__row'
        >
          <FormInput
            label='Title / Role'
            validationMessage={
              isBeingValidated
              && validation.fieldErrors.title?.length
                ? (
                  <ValidationMessageList
                    messages={validation.fieldErrors.title}
                  />
                )
                : undefined
            }
            onChange={(title) => customerReference.title = title}
            inputProps={{
              type: 'text',
              maxLength: 100,
              value: customerReference.title,
            }}
          />
          <FormInput
            required
            label='Email'
            validationMessage={
              isBeingValidated
              && validation.fieldErrors.emailAddress?.length
                ? (
                  <ValidationMessageList
                    messages={validation.fieldErrors.emailAddress}
                  />
                )
                : undefined
            }
            onChange={(emailAddress) => customerReference.emailAddress = emailAddress}
            inputProps={{
              type: 'text',
              maxLength: 100,
              value: customerReference.emailAddress,
            }}
          />
        </FlexBox>

        <FlexBox
          isFullWidth
          aligns='start'
          className='panel__row'
        >
          <PhoneNumberInput
            id={PhoneNumberField.CustomerReference}
            value={customerReference.phoneNumber}
            onChange={(phoneNumber) => {
              customerReference.phoneNumber = phoneNumber;
            }}
            onCountryChange={(id, newCountryCode) => {
              nominationWizardPageUiStore.setSelectedCountryCode(id, newCountryCode);
            }}
            error={
              isBeingValidated && validation.fieldErrors.phoneNumber?.length
                ? <ValidationMessageList messages={validation.fieldErrors.phoneNumber} />
                : undefined
            }
            label='Phone number'
            required
          />
          <FormInput
            required
            label='Company'
            validationMessage={
              isBeingValidated
              && validation.fieldErrors.companyName?.length
                ? (
                  <ValidationMessageList
                    messages={validation.fieldErrors.companyName}
                  />
                )
                : undefined
            }
            onChange={(companyName) => customerReference.companyName = companyName}
            inputProps={{
              type: 'text',
              maxLength: 100,
              value: customerReference.companyName,
            }}
          />
        </FlexBox>
        <FormInput
          label='Please add a brief description of what this reference can speak to'
          validationMessage={
            isBeingValidated
            && validation.fieldErrors.description?.length
              ? (
                <ValidationMessageList
                  messages={validation.fieldErrors.description}
                />
              )
              : undefined
          }
          onChange={(description) => customerReference.description = description}
          textAreaProps={{
            maxLength: 200,
            value: customerReference.description,
          }}
        />
      </>
    );
  }

  return (
    <AccordionItem
      className='customer-reference-form-inputs'
      value={rowKey}
    >
      <FlexBox
        isFullWidth
        content='end'
        className={`customer-reference-form-inputs__header ${isBeingValidated && !validation.isValid ? 'red-bg' : 'blue-bg'}`}
      >
        <FlexBox
          isFullWidth
          content='space-between'
          aligns='center'
        >
          <InfoLabel
            info={{
              children: getTooltipContentByFieldName('nomination-submission', 'customerReference'),
              className: commonStyles.tooltip,
            }}
          >
            <Text
              weight='semibold'
            >
              {`Customer Reference #${customerReferenceIndex + 1}`}
            </Text>
          </InfoLabel>
          <FlexBox
            aligns='center'
          >
            <Button
              icon={<DeleteRegular />}
              title='Delete'
              onClick={onRemoveCustomerReference}
              appearance='transparent'
            />
            <AccordionHeader
              expandIconPosition='end'
            />
          </FlexBox>
        </FlexBox>
      </FlexBox>
      <AccordionPanel
        className='customer-reference-form-inputs__panel'
      >
        {renderCustomerReferenceFormInputs()}
      </AccordionPanel>
    </AccordionItem>
  );
}

export const CustomerReferenceFormInputs = observer(CustomerReferenceFormInputsImpl);
